import { TrashIcon } from "@heroicons/react/24/outline";
import type { Control, FieldArray, FieldArrayPath, FieldValues } from "react-hook-form";
import { useFieldArray } from "react-hook-form";

import ButtonNewItem from "~/components/buttons/ButtonNewItem";
import FormSection from "~/components/formElements/FormSection";
import Input from "~/components/formElements/Input";
import Label from "~/components/formElements/Label";
import RadioGroup from "~/components/formElements/RadioGroup";
import { INVOICE_BY_MAIL_OPTIONS } from "~/constants/form.ts";
import BillingSettingsProjectValue
	from "~/modules/billing/components/formSections/components/BillingSettingsProjectValue.tsx";
import type { BillingSettingsDivergingFields } from "~/modules/billing/utils/billingSettingsDisplayUtils.ts";
import type { AdditionalEmailRecipient } from "~/modules/billing/utils/billingSettingsFormUtils.ts";

type Props<T extends FieldValues> = {
	control: Control<T>;
	fieldsDivergingFromProject?: BillingSettingsDivergingFields;
};

interface BillingInvoiceDeliveryFormData {
	additionalEmailRecipients: AdditionalEmailRecipient[];
	invoiceByMail: boolean;
}

const BillingInvoiceDeliveryFormSection = <T extends BillingInvoiceDeliveryFormData>({
	control,
	fieldsDivergingFromProject,
}: Props<T>) => {
	const { fields, append, remove } = useFieldArray({
		control,
		name: "additionalEmailRecipients" as FieldArrayPath<T>,
	});

	return (
		<FormSection title="Rechnungsversand">
			<div className="grid grid-cols-6 gap-x-6 gap-y-4">
				<div className="col-span-6">
					<RadioGroup
						control={control}
						label="Versand per"
						name="invoiceByMail"
						options={INVOICE_BY_MAIL_OPTIONS}
						numCols={2}
					/>
					{fieldsDivergingFromProject?.invoiceByMail.isDifferent &&
						<BillingSettingsProjectValue value={fieldsDivergingFromProject?.invoiceByMail.displayValue} />}
				</div>

				<div className="col-span-6 space-y-3">
					<Label>Zusätzliche E-Mail Empfänger</Label>
					{fields.map((field, index) => (
						<div key={field.id}
							 className="relative flex items-start gap-2 rounded-lg border p-4">
							<div className="flex flex-1 flex-col gap-2">
								<div>
									<Input
										control={control}
										label="E-Mail"
										name={`additionalEmailRecipients.${index}.email`}
									/>
								</div>
								<Input
									control={control}
									label="Name (optional)"
									name={`additionalEmailRecipients.${index}.name`}
								/>
							</div>
							<button
								type="button"
								className="group absolute right-4 top-3 flex flex-col items-center justify-center rounded bg-danger-500 p-1"
								onClick={() => remove(index)}
							>
								<TrashIcon className="size-4 text-white" />
							</button>
						</div>
					))}
					<ButtonNewItem theme="inline"
								   size="sm"
								   onClick={() => append({
									   email: "",
									   name: "",
								   } as FieldArray<T, FieldArrayPath<T>>, {})}>
						E-Mail-Empfänger hinzufügen
					</ButtonNewItem>
					{fieldsDivergingFromProject?.additionalEmailRecipients.isDifferent &&
						<BillingSettingsProjectValue value={fieldsDivergingFromProject?.additionalEmailRecipients.displayValue || "Keine"} />}
				</div>
			</div>
		</FormSection>
	);
};

export default BillingInvoiceDeliveryFormSection;